import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Flame } from "lucide-react";

export const ProcessTimeline = ({ data }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} id={data.id} className="py-14 sm:py-20 lg:py-28 bg-[#143C18] text-white relative overflow-hidden" data-testid="landing-process">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none grain" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
        <div className="max-w-2xl mb-14">
          <div className="chip chip-gold mb-4"><Flame className="w-3.5 h-3.5" /> {data.kicker}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08]">
            {data.heading[0]} <span className="block italic font-serif-alt text-[#E8BE4A]">{data.heading[1]}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative rounded-[32px] overflow-hidden aspect-[4/3] lg:aspect-[3/4] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] border border-white/10">
              <motion.img
                src={data.image}
                alt={data.imageAlt}
                style={{ y, scale: 1.16 }}
                width="1264"
                height="848"
                className="w-full h-full object-cover will-change-transform"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A12]/60 via-transparent to-transparent" />
            </div>
          </div>

          <ol className="lg:col-span-7 relative pl-2">
            <span aria-hidden className="absolute left-[1.35rem] sm:left-[1.6rem] top-4 bottom-4 w-px bg-gradient-to-b from-[#D4A017]/0 via-[#D4A017]/60 to-[#D4A017]/0" />
            {data.steps.map((s, i) => (
              <motion.li
                key={s.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative flex gap-5 sm:gap-7 pb-9 last:pb-0"
                data-testid={`process-step-${i}`}
              >
                <span className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#0F2A12] border border-[#D4A017]/60 flex items-center justify-center font-display font-bold text-[#E8BE4A] shrink-0 shadow-[0_0_0_6px_rgba(20,60,24,1)]">
                  {i + 1}
                </span>
                <div className="pt-1.5">
                  <h3 className="font-display text-xl sm:text-2xl font-bold">{s.title}</h3>
                  <p className="mt-1.5 text-white/75 leading-relaxed text-[15px] sm:text-base max-w-lg">{s.desc}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-3 text-sm text-white/70" data-testid="process-flow-line">
          {data.steps.map((s, i) => (
            <React.Fragment key={s.title}>
              <span className="font-semibold text-white/90">{s.title}</span>
              {i < data.steps.length - 1 && <span className="text-[#D4A017]">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};
